import { collection, doc, getDocsFromServer, query, where, runTransaction, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebaseConfig';
import { canCaptureDoctor } from './config';
import { cacheOwner } from './profileCache';
import { localStore } from './localStore';
import { assertViewerCanWrite } from '../utils/viewerOnly';
import axios from 'axios';
import API from '../config/api';
import { removeWaitingOnlyPatient, replaceDraftPatient, rememberPatients } from './profileCache';

const changed = () => window.dispatchEvent(new Event('offlineWaitingChanged'));
let syncing = false;
export function normalizeWaitingDate(value) {
  const match = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(String(value || '').trim());
  if (!match) return String(value || '').trim();
  return `${match[1]}-${match[2].padStart(2, '0')}-${match[3].padStart(2, '0')}`;
}
export async function pendingWaiting() {
  const owner = cacheOwner();
  if (!owner) return [];
  const rows = await localStore.list('waiting');
  return cacheOwner() === owner ? rows.filter(row => row.owner === owner) : [];
}
export async function queueWaitingPatient(clinicId, date, patient, operationId, options = {}) {
  assertViewerCanWrite();
  const owner = cacheOwner();
  if (!owner || !canCaptureDoctor(patient.doctor_id)) throw new Error('Offline waiting-list saving is not enabled for this doctor.');
  if (!clinicId || clinicId === '__all_clinics__' || !patient.patient_name?.trim()) throw new Error('Select a clinic and patient.');
  const normalizedDate = normalizeWaitingDate(date);
  const normalizedPatient = { ...patient, ...(patient.date ? { date: normalizeWaitingDate(patient.date) } : {}) };
  const key = `${owner}:${operationId}`;
  const previous = await localStore.get('waiting', key);
  if (!previous) await localStore.put('waiting', {
    key, owner, operationId, actorUid: auth.currentUser.uid, clinicId, date: normalizedDate,
    waitingDateKey: options.waitingDateKey || String(date || normalizedDate),
    patient: normalizedPatient, createPatient: options.createPatient === true, localPatientId: options.localPatientId || '',
    existingWaitingDocId: options.existingWaitingDocId || '', scope: options.scope || '',
    entered: options.entered === true,
    createdAt: Date.now(), status: 'pending', attempts: 0, nextAttemptAt: 0,
  });
  // A new patient belongs only to the waiting list until the doctor explicitly
  // presses Enter. Remove stale cache left by older builds, then cache the row
  // only when this action has actually entered the doctor room.
  if (options.createPatient && options.scope) {
    if (options.entered) await rememberPatients([normalizedPatient], options.scope);
    else await removeWaitingOnlyPatient(normalizedPatient, options.scope);
  }
  changed();
  void syncWaitingPatients();
  return { queued: true };
}
export async function markWaitingPatientEntered(patient) {
  const owner = cacheOwner();
  if (!owner) return null;
  const rows = await pendingWaiting();
  const action = rows.find(row => row.kind !== 'reminder' && (
    row.operationId === patient.id || row.existingWaitingDocId === patient.id
    || row.patient?._id === patient._id || row.patient?.patient_id === patient.patient_id
  ));
  if (!action) return null;
  const updated = { ...action, entered: true, patient: { ...action.patient, status: 'FINISHED' },
    status: 'pending', error: '', nextAttemptAt: 0 };
  await localStore.put('waiting', updated);
  if (updated.createPatient && updated.scope) await rememberPatients([updated.patient], updated.scope);
  changed();
  return updated.patient;
}
export async function queuePatientReminder(value) {
  assertViewerCanWrite();
  const owner = cacheOwner();
  if (!owner || !canCaptureDoctor(value.doctorId)) throw new Error('Offline reminders are not enabled.');
  const signature = JSON.stringify([value.patientId, value.visitDate, value.notes, value.reminderDurationDays]);
  if ((await pendingWaiting()).some(action => action.kind === 'reminder' && action.signature === signature)) return;
  const operationId = crypto.randomUUID();
  await localStore.put('waiting', { key: `${owner}:${operationId}`, operationId, actorUid: auth.currentUser.uid,
    owner, kind: 'reminder', signature, value, patient: { doctor_id: value.doctorId, patient_id: value.patientId, patient_name: value.patientName },
    createdAt: Date.now(), status: 'pending', attempts: 0, nextAttemptAt: 0 });
  changed();
}

export async function syncWaitingPatients() {
  if (syncing || !navigator.onLine || !cacheOwner()) return;
  syncing = true;
  try {
    const owner = cacheOwner();
    for (const action of (await pendingWaiting()).sort((a, b) => a.createdAt - b.createdAt)) {
      if (cacheOwner() !== owner || !navigator.onLine) break;
      const synchronizedDate = normalizeWaitingDate(action.date);
      if (synchronizedDate !== action.date) {
        action.date = synchronizedDate;
        if (action.patient?.date) action.patient.date = normalizeWaitingDate(action.patient.date);
        action.nextAttemptAt = 0;
        action.error = '';
        await localStore.put('waiting', action);
      }
      if (action.status === 'attention' || action.nextAttemptAt > Date.now()) continue;
      try {
        assertViewerCanWrite();
        // Older dashboard builds stored September dates as 2026-9-26. Repair
        // those durable actions during replay so existing patients can sync.
        let synchronizedPatient;
        let synchronizedRevision;
        if (action.createPatient && action.entered) {
          let clinicIds = [];
          try { clinicIds = JSON.parse(action.scope || '[]')?.[1] || []; } catch {}
          const request = { schemaVersion: 1, operationId: action.operationId, doctorId: action.patient.doctor_id,
            clinicId: action.clinicId, clinicIds, date: synchronizedDate, createPatient: true,
            patient: { ...action.patient, ...(action.patient.date ? { date: normalizeWaitingDate(action.patient.date) } : {}) } };
          let token = await auth.currentUser.getIdToken();
          const send = bearer => axios.post(`${API.BASE_URL}/api/offline/waiting-actions`, request,
            { headers: { Authorization: `Bearer ${bearer}` }, timeout: 20000 });
          let response;
          try { response = await send(token); } catch (error) {
            if (error.response?.status !== 401) throw error;
            token = await auth.currentUser.getIdToken(true); response = await send(token);
          }
          if (response.data?.operationId !== action.operationId || !response.data?.patient?._id) throw new Error('Missing backend waiting receipt');
          synchronizedPatient = response.data.patient;
          synchronizedRevision = response.data.revision;
        }
        if (action.kind === 'reminder') {
          const target = doc(db, 'nextVisits', action.patient.doctor_id, 'appointments', action.operationId);
          const receipt = doc(db, 'dashboardReminderReceipts', action.actorUid, 'actions', action.operationId);
          await runTransaction(db, async transaction => {
            const applied = await transaction.get(receipt);
            if (cacheOwner() !== owner) throw new Error('SESSION_CHANGED');
            if (applied.exists()) return;
            transaction.set(target, { ...action.value, offline_operation_id: action.operationId });
            transaction.set(receipt, { actorUid: action.actorUid, operationId: action.operationId, doctorId: action.patient.doctor_id, patientId: action.patient.patient_id });
          });
          await localStore.remove('waiting', action.key); changed();
          continue;
        }
        // Keep the Firestore path compatible with the assistant portal while
        // retaining a padded date for the backend patient record.
        const firestoreDate = action.waitingDateKey || synchronizedDate;
        const list = collection(db, 'clinics', action.clinicId, 'waiting_list', firestoreDate, 'patients');
        const target = doc(list, action.existingWaitingDocId || action.operationId);
        const current = await getDocsFromServer(query(list, where('doctor_id', '==', action.patient.doctor_id)));
        const maximum = current.docs.reduce((max, item) => Math.max(max, Number(item.data().position || item.data().user_order_in_queue) || 0), 0);
        const waitingPatient = { ...action.patient };
        for (const localField of ['_offlineDraft', '_offlineCreatedAt', 'offlinePending', 'id', '_id']) delete waitingPatient[localField];
        const synchronizedIdentity = synchronizedPatient ? {
          patient_mongo_id: synchronizedPatient._id,
          api_patient_id: synchronizedPatient.patient_id || action.patient.patient_id,
          file_number: synchronizedPatient.file_number || '',
          payment_type: synchronizedPatient.payment_type || waitingPatient.payment_type || 'cash',
          insurance_company_id: synchronizedPatient.insurance_company_id || '',
          insurance_company_name: synchronizedPatient.insurance_company_name || '',
          insurance_number: synchronizedPatient.insurance_number || '',
          coverage_percentage: Number(synchronizedPatient.coverage_percentage || 0),
          offline_patient_created: true,
        } : {};
        await runTransaction(db, async transaction => {
          const already = await transaction.get(target);
          if (cacheOwner() !== owner) throw new Error('SESSION_CHANGED');
          if (already.exists()) {
            const current = already.data();
            const recoverable = action.existingWaitingDocId && current.doctor_id === action.patient.doctor_id && current.patient_name === action.patient.patient_name;
            if (!recoverable && (current.offline_operation_id !== action.operationId || current.patient_id !== action.patient.patient_id || current.doctor_id !== action.patient.doctor_id)) {
              throw Object.assign(new Error('Operation identity conflict'), { code: 'OPERATION_ID_CONFLICT' });
            }
            if (recoverable && synchronizedPatient) transaction.set(target, {
              patient_id: action.patient.patient_id, ...synchronizedIdentity,
              offline_operation_id: action.operationId,
            }, { merge: true });
            if (action.entered) transaction.set(target, { status: 'FINISHED' }, { merge: true });
            return;
          }
          const position = maximum + 1;
          transaction.set(target, {
            ...waitingPatient, status: action.entered ? 'FINISHED' : 'WAITING', added_by: 'doctor', date: firestoreDate,
            position, user_order_in_queue: position, arrivalTime: serverTimestamp(),
            offline_operation_id: action.operationId, offline_created_at: action.createdAt,
            ...synchronizedIdentity,
          });
        });
        if (action.localPatientId && synchronizedPatient) await replaceDraftPatient(action.localPatientId, synchronizedPatient, synchronizedRevision);
        await localStore.remove('waiting', action.key);
        changed(); window.dispatchEvent(new Event('offlineProfileChanged'));
      } catch (error) {
        const terminal = ['permission-denied', 'unauthenticated', 'OPERATION_ID_CONFLICT', 'ERR_VIEWER_ONLY'].includes(error.code);
        const attempts = action.attempts + 1;
        await localStore.put('waiting', { ...action, attempts, status: terminal ? 'attention' : 'pending',
          error: error.code || 'CONNECTION_UNAVAILABLE', nextAttemptAt: Date.now() + Math.min(300000, 2000 * 2 ** Math.min(attempts, 7)) });
        changed();
      }
    }
  } finally { syncing = false; }
}
export async function retryWaitingPatient(action) {
  if (action.owner !== cacheOwner()) return;
  await localStore.put('waiting', { ...action, status: 'pending', nextAttemptAt: 0, error: '' });
  changed();
  await syncWaitingPatients();
}
